import { cn } from '@/shared/lib/cn';
import type { PropertyLabel } from '../../properties/propertyLabels';
import { labelFor } from '../../properties/propertyLabels';
import type { BookingListEntry } from '../components/bookingsGrouping';
import type { AgendaSection } from './agendaModel';
import { headOf, RAIL } from './agendaSectionHead';
import { BookingAgendaCard } from './BookingAgendaCard';
import { BookingAgendaGroup } from './BookingAgendaGroup';

interface Props {
  sections: AgendaSection[];
  propertyNames: ReadonlyMap<string, PropertyLabel>;
  canSeeGuests: boolean;
  today: string;
}

function Entry({ entry, ...rest }: { entry: BookingListEntry } & Omit<Props, 'sections'>) {
  return entry.kind === 'single' ? (
    <BookingAgendaCard
      booking={entry.row}
      apartment={labelFor(rest.propertyNames, entry.row.propertyId)}
      canSeeGuests={rest.canSeeGuests}
      today={rest.today}
    />
  ) : (
    <BookingAgendaGroup group={entry} {...rest} />
  );
}

const DIVIDER = { upcoming: 'Demnächst · nach Anreise', past: 'Vergangen' };

/** „Aktuell & kommend“ als Tagesliste (Board „Buchungen“): Heute, Im Haus, je Anreisetag, Vergangen. */
export function BookingAgenda({ sections, ...rest }: Props) {
  return (
    <div className="flex flex-col gap-5">
      {sections.map((section) => {
        const entries = (block: AgendaSection['blocks'][number]) =>
          block.entries.map((entry) => (
            <Entry
              key={entry.kind === 'single' ? entry.row.id : entry.reservationId}
              entry={entry}
              {...rest}
            />
          ));
        if (section.kind === 'flat') {
          return (
            <div key={section.key} className="flex flex-col gap-2.5">
              {section.blocks.map(entries)}
            </div>
          );
        }
        const head = headOf(section);
        return (
          <div key={section.key} className="flex flex-col gap-2.5">
            {section.divider && (
              <div className="flex items-center gap-3 pt-1.5">
                <span className="text-ink-soft font-mono text-[11px] font-semibold uppercase tracking-[0.16em]">
                  {DIVIDER[section.divider]}
                </span>
                <span className="bg-line h-px flex-1" />
              </div>
            )}
            <section
              aria-label={head.title}
              className="grid grid-cols-[64px_minmax(0,1fr)] items-start gap-x-3 gap-y-2.5 md:grid-cols-[104px_minmax(0,1fr)] md:gap-x-4"
            >
              <div
                className={cn(
                  'rounded-tile flex h-16 flex-col items-center justify-center md:row-span-2 md:h-[104px]',
                  RAIL[section.kind],
                )}
              >
                <span className="font-mono text-[9px] font-semibold uppercase tracking-[0.14em] opacity-85 md:text-[10px]">
                  {head.top}
                </span>
                <span className="font-serif text-[22px] font-bold leading-none tracking-[-0.04em] md:text-[40px]">
                  {head.big}
                </span>
                <span className="hidden text-xs font-semibold opacity-85 md:block">
                  {head.bottom}
                </span>
              </div>
              <div className="flex min-h-16 flex-col justify-center md:min-h-7 md:flex-row md:flex-wrap md:items-baseline md:justify-start md:gap-2.5">
                <h2 className="font-serif text-lg font-bold tracking-[-0.01em] md:text-[21px]">
                  {head.title}
                </h2>
                {head.hint && (
                  <span className="text-ink-soft text-xs md:text-[13px]">{head.hint}</span>
                )}
              </div>
              <div className="col-span-2 flex min-w-0 flex-col gap-2.5 md:col-span-1 md:col-start-2">
                {section.blocks.map((block) => (
                  <div key={block.tone ?? 'all'} className="flex flex-col gap-2">
                    {block.tone && (
                      <span
                        className={cn(
                          'flex items-center gap-2 text-xs font-bold',
                          block.tone === 'arrival' ? 'text-forest' : 'text-ink-soft',
                        )}
                      >
                        <span
                          aria-hidden
                          className={cn(
                            'h-2 w-[18px] -skew-x-[20deg] rounded-[4px]',
                            block.tone === 'arrival' ? 'bg-forest' : 'bg-ink-faint',
                          )}
                        />
                        {block.tone === 'arrival' ? 'Anreisen' : 'Abreisen'} ·{' '}
                        {block.entries.length}
                      </span>
                    )}
                    {entries(block)}
                  </div>
                ))}
              </div>
            </section>
          </div>
        );
      })}
    </div>
  );
}
